import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowUpRight, Camera, Image as ImageIcon, FileText, PenTool, TrendingUp, BarChart3, Sparkles, Check, Flame, Pin } from "lucide-react";
import SEO from "../components/SEO";
import AdSlot from "../components/AdSlot";
import useAds from "../hooks/useAds";

const iconMap = { TrendingUp, BarChart3, Camera, Image: ImageIcon, FileText, PenTool, Sparkles };

export default function Services() {
  const [services, setServices] = useState([]);
  const [hotTitle, setHotTitle] = useState(null);
  const [loading, setLoading] = useState(true);
  const { hasAds: hasSidebarAds } = useAds("services", "sidebar");

  useEffect(() => {
    Promise.all([
      api.get("/services"),
      api.get("/services/hot").catch(() => ({ data: { title: null } })),
    ]).then(([sRes, hRes]) => {
      setServices(sRes.data);
      setHotTitle(hRes.data?.title || null);
    }).finally(() => setLoading(false));
  }, []);

  // Sort order: manually pinned services first, then hot service, then by configured order
  const sortedServices = useMemo(() => {
    const pinned = services.filter((s) => s.pinned);
    const rest = services.filter((s) => !s.pinned);
    if (hotTitle) {
      const hotIdx = rest.findIndex((s) => s.title === hotTitle);
      if (hotIdx >= 0) {
        const [hot] = rest.splice(hotIdx, 1);
        return [...pinned, hot, ...rest];
      }
    }
    return [...pinned, ...rest];
  }, [services, hotTitle]);

  return (
    <div data-testid="services-page">
      <SEO
        title="Services — Menu Design, Food Photography & Brand Identity"
        description="Zomato profile optimization, menu score improvement, dish branding & food photography, profile thumbnail design, menu design, and logo & sticker design for restaurants."
        path="/services"
      />
      {/* header */}
      <section className="border-b border-black/5 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="text-sm uppercase tracking-[0.25em] text-[#525252] mb-4">Services</div>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tighter leading-none font-black max-w-3xl">
            Everything you need to win on <span className="text-[var(--brand-primary)]">Zomato & Swiggy</span>.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-[#525252]">
            From profile optimization to brand identity, we cover the full journey of how customers
            discover and choose your restaurant.
          </p>
        </div>
      </section>

      {/* Hero-banner ad slot */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 mt-8">
        <AdSlot page="services" slot="hero-banner" />
      </div>

      {/* services + sidebar ad */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {loading ? (
            <div className="text-[#525252]" data-testid="services-loading">Loading...</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className={hasSidebarAds ? "lg:col-span-9" : "lg:col-span-12"}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {sortedServices.map((s, idx) => {
                    const Ic = iconMap[s.icon] || Sparkles;
                    const isHot = hotTitle && s.title === hotTitle;
                    const isPinned = !!s.pinned;
                    const highlighted = isHot || isPinned;
                    const isFirstHighlight = highlighted && idx === 0;
                    return (
                      <div
                        key={s.id}
                        data-testid={`service-card-${s.slug}`}
                        className={`relative bg-white border p-8 lg:p-10 transition-colors group card-reveal card-reveal-${(idx % 6) + 1} ${
                          isFirstHighlight ? "md:col-span-2 lg:p-14" : ""
                        } ${
                          highlighted ? "border-[var(--brand-primary)]/40 ring-1 ring-[var(--brand-primary)]/20" : "border-black/5 hover:border-[var(--brand-primary)]/30"
                        }`}
                      >
                        {(isHot || isPinned) && (
                          <div className="absolute -top-3 left-8 inline-flex items-center gap-1.5 bg-[var(--brand-primary)] text-white text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-sm" data-testid="hot-service-badge">
                            {isPinned ? <><Pin size={12} strokeWidth={2.2} /> Pinned</> : <><Flame size={12} strokeWidth={2.2} /> Most Requested</>}
                          </div>
                        )}
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="font-mono text-xs text-[var(--brand-primary)]">
                              {String(idx + 1).padStart(2, "0")}
                            </div>
                            <Ic strokeWidth={1.5} size={26} className="text-[#0A0A0A]" />
                          </div>
                        </div>
                        <h3 className="mt-6 font-display text-3xl tracking-tight font-semibold">{s.title}</h3>
                        <p className="mt-3 text-[#525252] leading-relaxed">{s.description}</p>

                        {s.benefits?.length > 0 && (
                          <ul className="mt-6 space-y-2">
                            {s.benefits.map((b) => (
                              <li key={b} className="flex items-center gap-2 text-sm text-[#0A0A0A]">
                                <Check size={16} className="text-[var(--brand-primary)]" /> {b}
                              </li>
                            ))}
                          </ul>
                        )}

                        <div className="mt-8 flex items-center justify-between border-t border-black/5 pt-6">
                          {s.price_label ? (
                            <div className="font-mono text-sm">{s.price_label}</div>
                          ) : (
                            <div className="text-sm text-[#525252]">Custom pricing</div>
                          )}
                          <Link
                            to="/contact"
                            data-testid={`service-inquire-${s.slug}`}
                            className="inline-flex items-center gap-1 text-sm font-medium hover:text-[var(--brand-primary)]"
                          >
                            Inquire <ArrowUpRight size={16} />
                          </Link>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {hasSidebarAds && (
                <aside className="lg:col-span-3" data-testid="services-sidebar-ads">
                  <div className="lg:sticky lg:top-24">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#525252] mb-2">Sponsored</div>
                    <AdSlot page="services" slot="sidebar" />
                  </div>
                </aside>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
